import React, { useEffect, useState } from 'react';
import { Modal } from 'shards-react';
import ModalEditHeader from '../../common/ModalEditHeader';
import ModalEditBody from './ModalEditBody';
import ModalEditBodyDelete from './ModalEditBodyDelete';
import ModalEditFooter from '../../common/ModalEditFooter';
import { useTranslation } from 'react-i18next';
import useLocales from '../../../hooks/useLocales';
import { RootState, useDispatch, useSelector } from '../../../redux/store';
import { closeDialog, createOrganization, deleteOrganization, updateOrganization } from '../../../redux/slices/organization';
import getOrganizationDisplayName from '../../../utils/getOrganizationDisplayName';
import { EDIT_ACTION } from '../../../constants';
import type { BusinessType, SteelMaker, SteelCategory } from '../../../types/common';
import type { CreateOrganizationParams, DeleteOrganizationParams, OrganizationType, UpdateOrganizationParamsClient } from '../../../types/organization';
import type { OptionsType } from '../../../types/options';

const ModalEditOrganization = (): React.ReactElement => {
  const { t } = useTranslation();
  const { currentLang } = useLocales();
  const dispatch = useDispatch();
  const { dialogMode, isDialogOpen, organization, organizations } = useSelector((state: RootState) => state.organization);
  const { organizationBase, steelMaker, businessType, steelCategory } = useSelector((state: RootState) => state.options);
  const [selectedOrganizationBase, setSelectedOrganizationBase] = useState<OptionsType | null>(null);
  const [organizationSuffixName, setOrganizationSuffixName] = useState<string>('');
  const [selectedBusinessType, setSelectedBusinessType] = useState<BusinessType[]>([]);
  const [selectedSteelMaker, setSelectedSteelMaker] = useState<SteelMaker[]>([]);
  const [selectedSteelCategory, setSelectedSteelCategory] = useState<SteelCategory[]>([]);
  const [dialogTitle, setDialogTitle] = useState<string>('');
  const [buttonLabel, setButtonLabel] = useState<string>('');
  const [deleteFlag, setDeleteFlag] = useState<boolean>(false);

  // 削除モーダルで使う表示用の組織名
  const organizationName: string = organization ? getOrganizationDisplayName(organization, { organizationBases: organizationBase, language: currentLang.value }) : '';

  // 組織の名称（会社名＋サフィックス）がユニークかどうか
  const [isUniqueName, setIsUniqueName] = useState<boolean | undefined>(undefined);

  // すべて入力されているかどうか（OKボタンを押せるかどうかに使う）
  // ※organizationSuffixNameは空文字列でもOK
  const isFulfilled: boolean = selectedOrganizationBase !== null && selectedBusinessType.length > 0 && selectedSteelMaker.length > 0 && selectedSteelCategory.length > 0;

  /**
   * プルダウン選択肢のラベル表示関数
   * @param option[OptionsType] 選ばれた選択肢オブジェクト
   * @returns string ラベル文字列
   */
  const getOptionLabel = (option: OptionsType): string => (currentLang.value === 'ja' ? option.nameJapanese : option.nameEnglish ?? option.nameJapanese);

  /**
   * プルダウン選択肢のラベル表示関数
   * @param option[OptionsType] 選ばれた選択肢オブジェクト
   * @returns string ラベル文字列
   */
  const getOptionValue = (option: OptionsType): string => option.code;

  // SIDE EFFECT
  // ================================================================================

  /**
   * 確認用
   */
  // useEffect(() => {
  //   // console.log(selectedOrganizationBase);
  //   // console.log(organizationSuffixName);
  //   console.log(selectedSteelMaker);
  //   console.log(selectedBusinessType);
  //   console.log(selectedSteelCategory);
  //   // }, [selectedOrganizationBase, organizationSuffixName, selectedSteelMaker, selectedBusinessType, selectedSteelCategory]);
  // }, [selectedSteelMaker, selectedBusinessType, selectedSteelCategory]);

  /**
   * 新規作成モード以外では初期値を入れておく
   */
  useEffect(() => {
    // console.log(organization);
    if (dialogMode !== EDIT_ACTION.CREATE && organization) {
      const targetOrganizationBase: OptionsType | undefined = organizationBase.length > 0 ? organizationBase.find((base) => base.code === String(organization.organizationBaseId)) : undefined;
      setSelectedOrganizationBase(targetOrganizationBase);
      setOrganizationSuffixName(organization.organizationSuffixName);
      setSelectedSteelMaker(organization.steelMaker);
      setSelectedBusinessType(organization.businessType);
      setSelectedSteelCategory(organization.steelCategory);
    } else {
      // do nothing
    }
  }, [organization]);

  /**
   * モードに併せてタイトルとボタン名を設定
   */
  useEffect(() => {
    if (dialogMode === EDIT_ACTION.CREATE) {
      setDialogTitle(t('organization.dialog.titleCreate'));
      setButtonLabel(t('dialog.create'));
    } else if (dialogMode === EDIT_ACTION.UPDATE) {
      setDialogTitle(t('organization.dialog.titleUpdate'));
      setButtonLabel(t('dialog.update'));
    } else if (dialogMode === EDIT_ACTION.DELETE) {
      setDialogTitle(t('organization.dialog.titleDelete'));
      setButtonLabel(t('dialog.delete'));
    } else {
      setDialogTitle('');
      setButtonLabel('');
    }
  }, [dialogMode]);

  /**
   * 組織名の重複チェック
   */
  useEffect(() => {
    // organizationBaseとorganizationSuffixNameに何らかの値がある場合のみ判定を行う（両方揃わないとユニークかどうかがわからないため）
    if (selectedOrganizationBase !== null) {
      // 新規作成モードではorganizationsに同じorganizationBaseId + organizationSuffixNameのものがないかチェック
      if (dialogMode === EDIT_ACTION.CREATE) {
        const sameOrganization: OrganizationType | undefined =
          organizations.length > 0 && selectedOrganizationBase !== null
            ? organizations.find((org) => {
                const hasSameOrganization: boolean = org.organizationBaseId === Number(selectedOrganizationBase.code) && org.organizationSuffixName === organizationSuffixName;
                return hasSameOrganization;
              })
            : undefined;
        if (sameOrganization !== undefined || selectedOrganizationBase === null) {
          setIsUniqueName(false);
        } else {
          setIsUniqueName(true);
        }
        // 更新モードでは自分以外のorganizationsに同じorganizationBaseId + organizationSuffixNameのものがないかチェック
      } else if (dialogMode === EDIT_ACTION.UPDATE) {
        const sameOrganizationName: OrganizationType | undefined =
          organizations.length > 0 && selectedOrganizationBase !== null
            ? organizations.find((org) => {
                const hasSameOrganizationName: boolean =
                  org.id !== organization.id && org.organizationBaseId === Number(selectedOrganizationBase.code) && org.organizationSuffixName === organizationSuffixName;
                return hasSameOrganizationName;
              })
            : undefined;
        if (sameOrganizationName !== undefined || selectedOrganizationBase === null) {
          setIsUniqueName(false);
        } else {
          setIsUniqueName(true);
        }
        // 削除モードでは何もしない
      } else if (dialogMode === EDIT_ACTION.DELETE) {
        setIsUniqueName(undefined);
      } else {
        setIsUniqueName(undefined);
      }
    } else {
      // do nothing
    }
  }, [selectedOrganizationBase, organizationSuffixName]);

  // EVENT HANDLER
  // ================================================================================

  const resetModal = (): void => {
    dispatch(closeDialog());
    setSelectedOrganizationBase(undefined);
    setOrganizationSuffixName('');
    setSelectedBusinessType([]);
    setSelectedSteelMaker([]);
    setSelectedSteelCategory([]);
    setDialogTitle('');
    setButtonLabel('');
    setDeleteFlag(false);
  };

  /**
   * 組織名を変更した時
   */
  const handleOrganizationBaseChange = (option: OptionsType | null): void => {
    // console.log(option);
    setSelectedOrganizationBase(option);
  };

  /**
   * 組織詳細を変更した時
   */
  const handleOrganizationSuffixNameChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    // console.log(event);
    const newOrganizationName: string = event.currentTarget.value;
    setOrganizationSuffixName(newOrganizationName);
  };

  /**
   * 鉄鋼メーカーのチェックボックスを変更した時
   */
  const handleSteelMakerCheck = (codes: SteelMaker[]): void => {
    // console.log(option);
    setSelectedSteelMaker(codes);
  };

  /**
   * 区分のチェックボックスを変更した時
   */
  const handleBusinessTypeCheck = (codes: BusinessType[]): void => {
    // console.log(option);
    setSelectedBusinessType(codes);
  };

  /**
   * 分類のチェックボックスを変更した時
   */
  const handleSteelCategoryCheck = (codes: SteelCategory[]): void => {
    // console.log(option);
    setSelectedSteelCategory(codes);
  };

  /**
   * モーダルでキャンセルボタンを押した時
   */
  const handleCancel = (): void => {
    resetModal();
  };

  /**
   * モーダルでOKボタンを押した時
   */
  const handleSave = (): void => {
    // 新規作成時
    if (dialogMode === EDIT_ACTION.CREATE) {
      const params: CreateOrganizationParams = {
        organizationBaseId: Number(selectedOrganizationBase.code),
        organizationSuffixName,
        businessType: selectedBusinessType,
        steelMaker: selectedSteelMaker,
        steelCategory: selectedSteelCategory,
      };
      dispatch(createOrganization(params));

      // 更新時
    } else if (dialogMode === EDIT_ACTION.UPDATE && organization) {
      const params: UpdateOrganizationParamsClient = {
        id: organization.id,
        organizationBaseId: Number(selectedOrganizationBase.code),
        organizationSuffixName,
        businessType: selectedBusinessType,
        steelMaker: selectedSteelMaker,
        steelCategory: selectedSteelCategory,
      };
      dispatch(updateOrganization(params));

      // 削除時
    } else if (dialogMode === EDIT_ACTION.DELETE && organization) {
      const params: DeleteOrganizationParams = {
        id: organization.id,
      };
      dispatch(deleteOrganization(params));

      // その他
    } else {
      // do nothing
    }
    resetModal();
  };

  /**
   * モーダルでDeleteチェックをクリックした時
   */
  const handleToggleDelete = (): void => {
    setDeleteFlag(!deleteFlag);
  };

  // VIEW
  // ================================================================================

  return (
    <Modal id="modalEditOrganization" open={isDialogOpen} maxWidth={'md'} fullWidth={true} centered={true} toggle={handleCancel}>
      <ModalEditHeader iconName={'corporate_fare'} title={dialogTitle} />
      {/* 新規作成時・更新時 */}
      {(dialogMode === EDIT_ACTION.CREATE || dialogMode === EDIT_ACTION.UPDATE) && (
        <ModalEditBody
          labelBase={t('organizationBase.organizationBase')}
          base={selectedOrganizationBase}
          optionsBase={organizationBase}
          placeholderBase={t('dropdown.organizationBase')}
          getOptionLabel={getOptionLabel}
          getOptionValue={getOptionValue}
          onChangeBase={handleOrganizationBaseChange}
          labelName={t('organization.baseDetail')}
          name={organizationSuffixName}
          invalidName={isUniqueName === false}
          onChangeName={handleOrganizationSuffixNameChange}
          labelSteelMaker={t('steelMaker')}
          optionsSteelMaker={steelMaker}
          selectedSteelMaker={selectedSteelMaker}
          onChangeSteelMaker={handleSteelMakerCheck}
          labelBusinessType={t('businessType')}
          optionsBusinessType={businessType}
          selectedBusinessType={selectedBusinessType}
          onChangeBusinessType={handleBusinessTypeCheck}
          labelSteelCategory={t('steelCategory')}
          optionsSteelCategory={steelCategory}
          selectedSteelCategory={selectedSteelCategory}
          onChangeSteelCategory={handleSteelCategoryCheck}
        />
      )}

      {/* 削除時 */}
      {dialogMode === 'DELETE' && <ModalEditBodyDelete description={t('organization.dialog.card1')} label={t('organization.dialog.card2')} name={organizationName} />}

      <ModalEditFooter
        mode={dialogMode}
        theme={dialogMode === EDIT_ACTION.DELETE ? 'danger' : 'primary'}
        disabled={(dialogMode === EDIT_ACTION.CREATE && !isUniqueName) || (dialogMode === EDIT_ACTION.UPDATE && !isUniqueName) || (dialogMode === EDIT_ACTION.DELETE && !deleteFlag) || !isFulfilled}
        labelCancel={t('dialog.cancel')}
        labelSave={buttonLabel}
        labelCheckBox={t('dialog.validDelete')}
        deleteFlag={deleteFlag}
        onCancel={handleCancel}
        onSave={handleSave}
        onToggle={handleToggleDelete}
      />
    </Modal>
  );
};

export default ModalEditOrganization;
